import { z } from "zod/v4";

export const libraryScopeSchema = z.object({
  collectionId: z.uuid().optional(),
  assetIds: z.array(z.uuid()).min(1).max(500).optional(),
  search: z.string().trim().max(500).optional(),
});

export const libraryAnalysisSchema = libraryScopeSchema.extend({
  checks: z
    .array(z.enum(["descriptions", "duplicates", "quality"]))
    .min(1)
    .max(3),
});

export const analysisProgressSchema = z.object({
  afterId: z.uuid().optional(),
  scanned: z.number().default(0),
  proposed: z.number().default(0),
  failed: z.number().default(0),
  errors: z.array(z.string()).default(() => []),
  complete: z.boolean().default(false),
});

export const libraryAssetSnapshotSchema = z.object({
  collectionId: z.uuid(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  agentDescription: z.string().nullable(),
  mediaKey: z.string(),
  updatedAt: z.string(),
});

export const libraryChangeSchema = z.discriminatedUnion("action", [
  z.object({
    action: z.literal("describe"),
    assetId: z.uuid(),
    expected: libraryAssetSnapshotSchema,
    description: z.string().trim().min(1).max(2000),
  }),
  z.object({
    action: z.literal("move"),
    assetId: z.uuid(),
    expected: libraryAssetSnapshotSchema,
    collectionId: z.uuid(),
  }),
  z.object({
    action: z.literal("remove"),
    assetId: z.uuid(),
    expected: libraryAssetSnapshotSchema,
    keeperId: z.uuid().optional(),
    keeperExpected: libraryAssetSnapshotSchema.optional(),
  }),
  z.object({
    action: z.literal("create_collection"),
    name: z.string().trim().min(1).max(100),
    mediaKind: z.enum(["image", "video", "component", "any"]),
    brandProfileIds: z.array(z.uuid()).max(100),
  }),
  z.object({
    action: z.literal("rename_collection"),
    collectionId: z.uuid(),
    expectedName: z.string(),
    name: z.string().trim().min(1).max(100),
  }),
  z.object({
    action: z.literal("quality"),
    assetId: z.uuid(),
    expected: libraryAssetSnapshotSchema,
    issues: z.array(z.string().max(500)).min(1).max(10),
  }),
]);

export const libraryFindingSchema = z.object({
  change: libraryChangeSchema,
  summary: z.string(),
});
export type LibraryFinding = z.infer<typeof libraryFindingSchema>;

export type LibraryScope = z.infer<typeof libraryScopeSchema>;
export type LibraryAnalysis = z.infer<typeof libraryAnalysisSchema>;
export type LibraryChange = z.infer<typeof libraryChangeSchema>;
export type LibraryAssetSnapshot = z.infer<typeof libraryAssetSnapshotSchema>;

export const LIBRARY_PAGE_SIZE = 50;
